import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, MapPin, PlusCircle } from "lucide-react";

/**
 * Property address autocomplete.
 * - Fetches properties once
 * - Filters as user types
 * - Fires onSelect(property) when a suggestion is picked
 * - Optional onCreateNew(text) when nothing matches
 */
export default function PropertyAutocomplete({
  onSelect,
  onCreateNew,
  placeholder = "Type a property address…",
  autoFocus = false,
  testid = "property-autocomplete",
}) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(0);
  const wrap = useRef(null);

  useEffect(() => { api.get("/properties").then(r => setItems(r.data)); }, []);
  useEffect(() => {
    const onClick = (e) => { if (!wrap.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = q.trim() === "" ? [] : items.filter(p => {
    const hay = `${p.address} ${p.city} ${p.county} ${p.zip_code || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  }).slice(0, 8);

  const pick = (p) => {
    setQ(`${p.address}, ${p.city}`);
    setOpen(false);
    onSelect?.(p);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHover(h => Math.min(h + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHover(h => Math.max(h - 1, 0)); }
    if (e.key === "Enter") {
      e.preventDefault();
      if (results[hover]) pick(results[hover]);
      else if (onCreateNew) { onCreateNew(q); setOpen(false); }
    }
    if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={wrap} className="relative w-full">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <Input
          data-testid={testid}
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHover(0); }}
          onFocus={() => setOpen(q.length > 0)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="pl-9 h-11"
        />
      </div>
      {open && q.trim() && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden">
          {results.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">
              No property matches "{q}".
              {onCreateNew && (
                <button
                  data-testid="autocomplete-create-new"
                  onClick={() => { onCreateNew(q); setOpen(false); }}
                  className="mt-2 flex items-center gap-1.5 text-[#C25E00] hover:underline"
                >
                  <PlusCircle className="w-3.5 h-3.5" /> Create new property
                </button>
              )}
            </div>
          ) : (
            results.map((p, i) => (
              <button
                key={p.id}
                data-testid={`autocomplete-option-${p.id}`}
                onMouseEnter={() => setHover(i)}
                onClick={() => pick(p)}
                className={`w-full text-left px-3 py-2 flex items-start gap-2 border-b border-slate-100 last:border-b-0 ${i === hover ? "bg-slate-50" : "bg-white"}`}
              >
                <MapPin className="w-3.5 h-3.5 mt-1 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{p.address}</div>
                  <div className="text-[11px] text-slate-500 truncate">{p.city}, TX {p.zip_code || ""} · {p.county} County · {p.bedrooms}BR/{p.bathrooms}BA</div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
